import { useCallback, useMemo, useState } from "react";
import FileSaver from "file-saver";
import dateformat from "dateformat";
import { createUseStyles } from "react-jss";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import AddIcon from "@mui/icons-material/Add";
import { Button as UIButton } from "@mui/material";

import InfoContainer from "$components/InfoContainer";
import { Button, Modal, Text } from "$components";
import JTable from "$components/JTable/JTable";
import {
  ShippingAddressEntityType,
  ShippingFile,
  ShippingRecord,
  Shipment,
} from "$types";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import useJStyles from "$hooks/useJStyles";
import useMedia from "$hooks/useMedia";
import useShippingFile from "$hooks/useShippingFile";
import { getShipmentStatusLabel } from "$utils/typeConstants/Shipment";
import {
  canAddProofOfDelivery,
  getDeliveryLocationShipmentsWithProof,
  getProofFileForShipment,
} from "$utils/typeConstants/ShippingRecordProofOfDelivery";
import AddProofOfDeliveryPopin from "./AddProofOfDeliveryPopin";

type Props = {
  shippingRecord: ShippingRecord;
  classes: any;
  fetchShippingRecord: () => void;
  disabled?: boolean;
  isPublic?: boolean;
};

function ShippingRecordDeliveryTrackingBlock({
  classes,
  shippingRecord,
  fetchShippingRecord,
  disabled = false,
  isPublic = false,
}: Props) {
  const { deleteMedia } = useMedia("shipping");
  const { deleteShippingFile } = useShippingFile(shippingRecord?.id);
  const [isAddProofDrawerOpen, toggleAddProofDrawer] = useState<boolean>(false);
  const [isDeleteModalOpen, toggleDeleteModal] = useState<boolean>(false);
  const [selectedProofFile, setSelectedProofFile] = useState<ShippingFile>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const isHandledByExternalConnector = false;

  const localClasses = useStyles();
  const { jclasses } = useJStyles();

  const deliveryShipmentsWithProof = useMemo(
    () => getDeliveryLocationShipmentsWithProof(shippingRecord),
    [shippingRecord]
  );

  const handleDeleteProofOfDelivery = (proofFile: ShippingFile) => {
    setSelectedProofFile(proofFile);
    toggleDeleteModal(true);
  };

  const handleFileDelete = async () => {
    if (!selectedProofFile?.id) {
      return;
    }

    setIsSubmitting(true);
    toggleDeleteModal(false);
    await deleteShippingFile(selectedProofFile.id);
    if (selectedProofFile.mainApiMediaId) {
      await deleteMedia(selectedProofFile.mainApiMediaId);
    }
    fetchShippingRecord();
    setIsSubmitting(false);
  };

  const renderLocationLabel = useCallback((shipment: Shipment) => {
    const zipCity = `${shipment.toAddress?.zip || ""} ${
      shipment.toAddress?.city || ""
    }`;

    if (
      shipment.toAddress?.entityType === ShippingAddressEntityType.WAREHOUSE
    ) {
      return (
        <div>
          <Text type="important">
            {shipment.toAddress?.name || shipment.op?.shipEntrepotName || ""}
          </Text>
          <div>{zipCity}</div>
        </div>
      );
    }
    if (shipment.toAddress?.entityType === ShippingAddressEntityType.DOCKYARD) {
      return (
        <div>
          <div>
            <Text type="important">{shipment.op?.chantier?.code}</Text>
            {` ${
              shipment.op?.chantier?.name || shipment.toAddress?.name || ""
            }`}
          </div>
          <div>{zipCity}</div>
        </div>
      );
    }

    return (
      <div>
        <div>
          <Text type="important">
            {shipment.toAddress?.name || shipment.op?.shipPlace || ""}
          </Text>
        </div>
        <div>{zipCity}</div>
      </div>
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "Lieu de livraison",
        minWidth: 150,
        maxWidth: 150,
        Cell: ({
          row: { original: shipment },
        }: {
          row: { original: Shipment };
        }) => {
          return <Text>{renderLocationLabel(shipment)}</Text>;
        },
      },
      {
        Header: "Statut",
        minWidth: 100,
        maxWidth: 100,
        hidden: !isHandledByExternalConnector,
        Cell: ({
          row: { original: shipment },
        }: {
          row: { original: Shipment };
        }) => {
          return <Text>{getShipmentStatusLabel(shipment.status)}</Text>;
        },
      },
      {
        Header: "Preuve de livraison",
        minWidth: 150,
        maxWidth: 150,
        Cell: ({
          row: { original: shipment },
        }: {
          row: { original: Shipment };
        }) => {
          const proofFile = getProofFileForShipment(shipment, shippingRecord);

          return (
            <div className={localClasses.proofOfDeliveryContainer}>
              {proofFile && (
                <>
                  <Button
                    variant="text"
                    isDefault
                    disabled={isSubmitting}
                    onClick={() =>
                      FileSaver.saveAs(
                        proofFile.url,
                        proofFile.name || "preuve_livraison.pdf"
                      )
                    }
                  >
                    Voir la preuve
                  </Button>
                  <Text type="disabled">
                    {`(ajoutée le ${dateformat(
                      proofFile.createdAt,
                      DEFAULT_DATE_FORMAT
                    )})`}
                  </Text>
                </>
              )}
            </div>
          );
        },
      },
      {
        Header: "Actions",
        minWidth: 100,
        maxWidth: 100,
        Cell: ({
          row: { original: shipment },
        }: {
          row: { original: Shipment };
        }) => {
          const proofFile = getProofFileForShipment(shipment, shippingRecord);

          return (
            <div>
              {!isHandledByExternalConnector && !isPublic && proofFile && (
                <Button
                  disabled={isSubmitting || disabled}
                  variant="outlined"
                  onClick={() => handleDeleteProofOfDelivery(proofFile)}
                >
                  Supprimer une preuve
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [
      isSubmitting,
      localClasses,
      renderLocationLabel,
      disabled,
      isHandledByExternalConnector,
      isPublic,
      shippingRecord,
    ]
  );

  return (
    <>
      <InfoContainer
        className={classes.paperContainer}
        isToggleable
        leftHeader={
          <div className={classes.infoHeader}>
            <LocalShippingOutlinedIcon />
            <Text type="h3">Suivi de livraison(s)</Text>
          </div>
        }
        rightHeader={
          !isPublic ? (
            <UIButton
              style={{ textTransform: "none" }}
              onClick={() => toggleAddProofDrawer(true)}
              disabled={!canAddProofOfDelivery(shippingRecord) || disabled}
              className={classes.textButton}
            >
              <AddIcon />
              Ajouter une preuve
            </UIButton>
          ) : null
        }
      >
        <div>
          <JTable
            data={deliveryShipmentsWithProof}
            columns={columns}
            setFilter={() => {}}
            setPageIndex={() => {}}
            pageCount={1}
            pageIndex={0}
            hideCheckboxes
            hideFilters
            noResultsText="Aucune preuve de livraison dans ce dossier"
          />
        </div>
      </InfoContainer>
      <AddProofOfDeliveryPopin
        isOpen={!disabled && isAddProofDrawerOpen}
        onClose={() => toggleAddProofDrawer(false)}
        onSubmit={fetchShippingRecord}
        shippingRecord={shippingRecord}
      />
      <Modal
        title="Supprimer la preuve de livraison"
        textValidate="Oui"
        textCancel="Non"
        open={isDeleteModalOpen}
        onValidate={handleFileDelete}
        onCancel={() => toggleDeleteModal(false)}
      >
        <div className={jclasses.dialogContent} style={{ textAlign: "center" }}>
          <Text>
            Attention
            <br />
            Vous êtes sur le point de supprimer cette preuve de livraison.
            <br />
            Êtes-vous sûr de vouloir continuer ?
          </Text>
        </div>
      </Modal>
    </>
  );
}

export default ShippingRecordDeliveryTrackingBlock;

const useStyles = createUseStyles(() => ({
  proofOfDeliveryContainer: {
    display: "flex",
    flexDirection: "column",
  },
}));
